import { useCallback } from 'react';
import type { GoodsOption } from 'shared/src/api';
import { useProductCheckout } from '../queries/useProductCheckout';
import { useAutoRedeemBeforeCheckout } from './useAutoRedeemBeforeCheckout';
import { useLogClickCheckOut } from './useLogClickCheckOut';
import { useMyInfo } from 'shared/src/queries';
import { useOpenDeeplink } from 'shared/src/hooks';
import { bridge, getWebBaseURL, isNil, isWeb } from 'shared/src/utils';
import { ApiError } from 'shared/src/api/httpClient';
import { systemAlert } from 'shared/src/component';
import type { ProductOptionMaxDiscountMode } from '../../../hooks/useProductOptionMaxDiscountMode';

export const useOnPressPurchase = ({
  selected,
  onCompletePurchase,
  onAnonymouseUserPressedPurchase,
  mode = 'disabled',
  disabled = false,
}: {
  selected: GoodsOption[];
  onCompletePurchase: () => void;
  onAnonymouseUserPressedPurchase: () => void;
  mode?: ProductOptionMaxDiscountMode;
  disabled?: boolean;
}) => {
  const { mutateAsync: checkoutProduct, isPending: loadingCheckout } =
    useProductCheckout(selected);
  const { autoRedeemBeforeCheckout, loadingAutoRedeem } =
    useAutoRedeemBeforeCheckout({ selected, mode });
  const { data: myInfo } = useMyInfo();
  const logClickCheckOut = useLogClickCheckOut({ selected });
  const { openDeeplink } = useOpenDeeplink();

  const onPressPurchase = useCallback(async () => {
    if (disabled) return;
    if (myInfo?.is_anonymous) {
      onAnonymouseUserPressedPurchase();
      return;
    }
    logClickCheckOut();
    try {
      const { pricing, redeemedCouponSnos } = await autoRedeemBeforeCheckout();
      if (isNil(pricing)) return;
      const result = await checkoutProduct({ pricing, redeemedCouponSnos });

      if (result) {
        if (isWeb) {
          const baseURL = await getWebBaseURL();
          location.href = `${baseURL}/payment/checkout/${result.checkout_key}`;
        } else {
          openDeeplink(result.deeplink);
        }
        onCompletePurchase();
      }
    } catch (ex) {
      if (ex instanceof ApiError) {
        if (ex.type === 'ALERT') {
          systemAlert(ex.message);
        } else {
          bridge.toast(ex.message);
        }
      }
    }
  }, [
    autoRedeemBeforeCheckout,
    checkoutProduct,
    disabled,
    logClickCheckOut,
    myInfo?.is_anonymous,
    onAnonymouseUserPressedPurchase,
    onCompletePurchase,
    openDeeplink,
  ]);

  return {
    onPressPurchase,
    loadingProductCheckout: loadingCheckout || loadingAutoRedeem,
  };
};
